import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Heart, Clock, MapPin, Sparkles, ArrowRight, Leaf, ArrowDown } from "lucide-react";
import { ASSETS, PRACTITIONER, QHHT_PRINCIPLES, SESSION_FACTS, EXPECT_STEPS } from "@/data/content";
import { Reveal, PrimaryButton, SecondaryButton } from "@/components/primitives";
import RichText from "@/components/RichText";
import SafetyNote from "@/components/SafetyNote";
import Sprig from "@/components/Sprig";
import Snippet from "@/components/Snippet";
import { useSite } from "@/context/SiteContext";

const FACT_ICONS = [Heart, Sparkles, Clock, Leaf];
const EASE = [0.22, 1, 0.36, 1];

/* Word-by-word masked reveal for the hero headline (CMS-driven text). */
function MaskedTitle({ text, className = "" }) {
  const reduce = useReducedMotion();
  const words = (text || "").split(" ").filter(Boolean);
  return (
    <h1 className={className} data-testid="hero-title" aria-label={text}>
      {words.map((w, i) => (
        <span key={i} aria-hidden="true" className="inline-block overflow-hidden align-bottom pr-[0.22em] pb-[0.14em]">
          <motion.span
            className="inline-block"
            initial={reduce ? { y: 0 } : { y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay: 0.35 + i * 0.09, ease: [0.76, 0, 0.24, 1] }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

export default function Home() {
  const { page, img, settings, snip } = useSite();
  const reduce = useReducedMotion();
  const p = page("home");
  const hero = p.hero || {};
  const cardImgs = [
    img("home_card_1", ASSETS.cardLavender),
    img("home_card_2", ASSETS.cardStillness),
    img("home_card_3", ASSETS.cardShore),
  ];

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, reduce ? 1.08 : 1.22]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-14%"]);
  const fgOpacity = useTransform(scrollYProgress, [0, 0.7], [1, reduce ? 1 : 0]);

  const cards = (p.cards && p.cards.length ? p.cards : QHHT_PRINCIPLES).slice(0, 3);

  return (
    <div className="bg-brand-bg">
      {/* ————— KINETIC HERO ————— */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
          <img
            src={img("home_hero", ASSETS.hero)}
            alt="South Devon coastline at first light"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/40 to-brand-bg/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-bg/55 via-transparent to-transparent" />
        </motion.div>

        <motion.div style={{ y: fgY, opacity: fgOpacity }} className="relative w-full max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28 pt-40">
          <motion.span
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="flex items-center gap-4 font-script text-3xl md:text-5xl text-brand-lavender-dark"
          >
            {hero.script || "An invitation"}
            <Sprig className="h-10 md:h-12" />
          </motion.span>

          <MaskedTitle
            text={hero.title || "Free QHHT volunteer sessions for women"}
            className="mt-3 max-w-5xl font-heading text-[3.25rem] leading-[1.12] sm:text-7xl lg:text-[6.5rem] font-medium text-brand-ink tracking-[-0.02em]"
          />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-9 flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-16"
          >
            <p className="font-body text-base md:text-lg text-brand-slate leading-relaxed max-w-xl">
              {hero.intro ||
                "The Quantum Healing Hypnosis Technique, developed by Dolores Cannon. Isabelle offers complimentary sessions to female volunteers in a calm, safe and supportive space near Ivybridge, South Devon."}
            </p>
            <div className="flex flex-wrap gap-4 shrink-0">
              <PrimaryButton as={Link} to="/volunteer" data-testid="hero-volunteer-btn" className="hover:-translate-y-0.5">
                Volunteer for a free session <ArrowRight size={18} strokeWidth={1.5} />
              </PrimaryButton>
              <SecondaryButton as={Link} to="/about-qhht" data-testid="hero-learn-btn">
                What is QHHT?
              </SecondaryButton>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 font-body text-sm text-brand-slate border-t border-brand-line pt-6"
          >
            <span className="flex items-center gap-2"><MapPin size={16} strokeWidth={1.5} /> {settings.location || PRACTITIONER.location}</span>
            <span className="flex items-center gap-2"><Heart size={16} strokeWidth={1.5} /> <Snippet k="home.meta_women" fallback="Women only · limited spaces" /></span>
            <span className="hidden md:flex items-center gap-2 text-brand-forest/80"><ArrowDown size={16} strokeWidth={1.5} className="animate-float-slow" /> <Snippet k="home.meta_scroll" fallback="Scroll to begin" /></span>
          </motion.div>
        </motion.div>
      </section>

      {/* ————— INTRO / BODY (sticky editorial) ————— */}
      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[0.85fr,1.15fr] gap-14 lg:gap-20">
          <div className="lg:sticky lg:top-32 self-start">
            <Reveal>
              <span className="font-body text-xs uppercase tracking-[0.3em] text-brand-lavender-dark"><Snippet k="home.welcome_eyebrow" fallback="Welcome" /></span>
              <p className="mt-5 font-heading text-3xl sm:text-4xl text-brand-forest italic leading-[1.15]">
                <Snippet k="home.welcome_heading" fallback="A quiet space to remember what you already know." />
              </p>
              <div className="mt-8 h-px w-24 bg-brand-lavender" />
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            {p.body_html ? (
              <RichText html={p.body_html} className="max-w-2xl" />
            ) : (
              <div className="max-w-2xl">
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-ink leading-[1.1]">
                  A peaceful space to listen to your inner wisdom
                </h2>
                <p className="mt-6 font-body text-base md:text-lg text-brand-slate leading-relaxed">
                  QHHT is a gentle, guided form of deep relaxation that helps you reconnect with the wise, knowing part of yourself.
                </p>
              </div>
            )}
            <div className="mt-8">
              <Link to="/what-to-expect" className="inline-flex items-center gap-2 font-body text-brand-forest hover:gap-3.5 transition-all" data-testid="intro-expect-link">
                <Snippet k="home.intro_link" fallback="See what to expect" /> <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————— NUMBERED MANIFESTO CHAPTERS ————— */}
      <section className="py-20 md:py-28 bg-white border-y border-brand-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <span className="font-body text-xs uppercase tracking-[0.3em] text-brand-lavender-dark"><Snippet k="home.journey_eyebrow" fallback="The journey" /></span>
                <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-ink leading-[1.02]">
                  <Snippet k="home.journey_heading" fallback="Four gentle chapters, at your own pace" />
                </h2>
              </div>
              <p className="font-body text-brand-slate max-w-sm">
                <Snippet k="home.journey_intro" fallback="Nothing is rushed and nothing is forced. Here is how a session unfolds, from your first breath to your last." />
              </p>
            </div>
          </Reveal>

          <div className="divide-y divide-brand-line">
            {[1, 2, 3, 4].map((n, i) => (
              <Reveal key={n} delay={i * 0.06}>
                <div className="group grid md:grid-cols-[auto,1fr,1.3fr] items-baseline gap-4 md:gap-10 py-8 md:py-10 transition-colors">
                  <span className="font-heading text-5xl md:text-7xl text-brand-lavender-light group-hover:text-brand-lavender transition-colors duration-500 tabular-nums leading-none">
                    {`0${n}`}
                  </span>
                  <h3 className="font-heading text-2xl md:text-3xl text-brand-ink"><Snippet k={`home.step${n}_title`} fallback={EXPECT_STEPS[i]?.title} /></h3>
                  <p className="font-body text-brand-slate leading-relaxed"><Snippet k={`home.step${n}_body`} fallback={EXPECT_STEPS[i]?.body} /></p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— HEART OF QHHT — STAGGERED CLIPPED FRAMES ————— */}
      <section className="py-24 md:py-36 bg-brand-accent/40 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-20">
              <span className="font-body text-xs uppercase tracking-[0.3em] text-brand-lavender-dark"><Snippet k="home.heart_eyebrow" fallback="The heart of QHHT" /></span>
              <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-ink leading-[1.05]">
                <Snippet k="home.heart_heading" fallback="What this gentle journey offers you" />
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-10" data-testid="home-cards">
            {cards.map((c, i) => (
              <Reveal key={c.title || i} delay={i * 0.12} className={i === 1 ? "md:translate-y-14" : ""}>
                <article className="group" data-testid={`home-card-${i}`}>
                  <div className="arch-frame relative aspect-[3/4] shadow-[0_24px_70px_rgba(59,92,69,0.16)]">
                    <img
                      src={cardImgs[i % cardImgs.length]}
                      alt=""
                      className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-forest-dark/40 via-transparent to-transparent" />
                  </div>
                  <h3 className="mt-7 font-heading text-2xl md:text-[1.7rem] text-brand-ink leading-tight">{c.title}</h3>
                  <p className="mt-3 font-body text-brand-slate leading-relaxed">{c.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— PORTAL DIVIDER + FACTS ————— */}
      <section className="relative py-28 md:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img src={img("qhht_divider", ASSETS.canopy)} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-brand-forest-dark/72" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white text-center font-medium max-w-3xl mx-auto leading-[1.12]">
              <Snippet k="home.portal_heading" fallback="About the free volunteer session" />
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 gap-5">
            {SESSION_FACTS.map((f, i) => {
              const Icon = FACT_ICONS[i];
              return (
                <Reveal key={f.label} delay={i * 0.1}>
                  <div className="flex items-start gap-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/[0.16] transition-colors">
                    <div className="h-11 w-11 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                      <Icon className="text-white" size={20} strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-body text-white font-medium"><Snippet k={`home.fact${i + 1}_label`} fallback={f.label} /></p>
                      <p className="font-body text-sm text-white/75 mt-1"><Snippet k={`home.fact${i + 1}_detail`} fallback={f.detail} /></p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <PrimaryButton as={Link} to="/volunteer" data-testid="portal-volunteer-btn" className="hover:-translate-y-0.5">
              <Snippet k="home.portal_cta" fallback="I'd love to volunteer" /> <ArrowRight size={18} strokeWidth={1.5} />
            </PrimaryButton>
          </div>
        </div>
      </section>

      {/* ————— ABOUT ISABELLE TEASER ————— */}
      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal>
            <div className="relative">
              <div className="arch-frame aspect-[4/5] max-w-md shadow-[0_24px_70px_rgba(59,92,69,0.16)]">
                <img src={img("about_isabelle_photo", ASSETS.womanField)} alt="Isabelle in nature" className="h-full w-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-2 sm:right-6 bg-white rounded-3xl p-6 shadow-lg border border-brand-line max-w-[220px]">
                <span className="font-script text-2xl text-brand-lavender-dark">with love</span>
                <p className="font-body text-sm text-brand-slate mt-1">Held in a safe, women-only space.</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <span className="font-script text-3xl text-brand-lavender-dark"><Snippet k="home.isabelle_script" fallback="My name is Isabelle" /></span>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-ink leading-[1.1]">
              <Snippet k="home.isabelle_heading" fallback="Walking this path with you" />
            </h2>
            <p className="mt-6 font-body text-base md:text-lg text-brand-slate leading-relaxed">
              <Snippet k="home.isabelle_body" fallback="I am completing my Level 2 training in the Quantum Healing Hypnosis Technique developed by Dolores Cannon. It would be my honour to guide you gently through this experience in a relaxed, one-to-one setting." />
            </p>
            <div className="mt-8">
              <SecondaryButton as={Link} to="/about-isabelle" data-testid="home-about-isabelle-btn">
                <Snippet k="home.isabelle_cta" fallback="More about Isabelle" />
              </SecondaryButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————— SAFETY ————— */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <SafetyNote />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
